import React, { useEffect, useState } from 'react';
import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  Button,
  Spinner,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Select,
  Image as ChakraImage,
  Input,
} from '@chakra-ui/react';
import { FaCheckCircle } from 'react-icons/fa';
import { getProducts, publishProduct, getProduct as fetchProduct, generateMockup, getMockupStatus } from '../services/printifyService';
import { useCart } from '../context/CartContext';
import { useLocation } from 'react-router-dom';
import supabase from '../utils/supabaseUtils';

// Temporary package data – can be replaced with dynamic values later
const DEFAULT_PACKAGES = [
  { id: 'basic',    name: 'Basic',        price: 30,  imageCount: 1, description: '1 digital image',   features: ['1 professionally-edited image', 'High-resolution download'] },
  { id: 'premium',  name: 'Premium',      price: 50,  imageCount: 2, description: '2 digital images',  features: ['2 professionally-edited images', 'High-resolution downloads', 'Social-media crops'] },
  { id: 'ultimate', name: 'Ultimate',     price: 65,  imageCount: 3, description: '3 digital images',  features: ['3 professionally-edited images', 'High-resolution downloads', 'Social-media crops', 'Priority turnaround'] },
  { id: 'pro',      name: 'Pro Bundle',   price: 80,  imageCount: 5, description: '5 digital images',  features: ['5 professionally-edited images', 'High-resolution downloads', 'Social-media crops', 'Priority turnaround'] },
  { id: 'frame',    name: 'Frame Package',price: 100, imageCount: 3, description: '3 digital images + framed print', features: ['3 digital images', 'High-resolution downloads', '14″ × 18″ framed print'] },
];
const DISCOUNT = 0.5;

const shopId = import.meta.env.VITE_PRINTIFY_SHOP_ID; // optional – only needed for product listing

const PackagesPage = () => {
  const [printifyProducts, setPrintifyProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [currentDetails, setCurrentDetails] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState('');
  const [selectedImageUrl, setSelectedImageUrl] = useState('');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { addToCart } = useCart();
  const location = useLocation();
  const [userImages, setUserImages] = useState([]);
  const [mockupTaskId, setMockupTaskId] = useState(null);
  const [mockupLoading, setMockupLoading] = useState(false);
  const [mockupImage, setMockupImage] = useState('');

  useEffect(() => {
    const fetchProducts = async () => {
      if (!shopId) return; // skip when no shop id configured
      try {
        setLoadingProducts(true);
        const res = await getProducts(shopId, { limit: 20, status: 'published' });
        setPrintifyProducts(res?.data || res);
      } catch (err) {
        console.error('Failed to load Printify products:', err);
      } finally {
        setLoadingProducts(false);
      }
    };

    fetchProducts();
  }, []);

  // If the page is opened with ?src=<url>, pre-select the image
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const preload = params.get('src');
    if (preload) {
      setSelectedImageUrl(preload);
      // Automatically open the first physical-product customise modal
      // if that feels right for your UX
      // openProductModal(printifyProducts[0]);
    }
  }, [location.search]);

  const handleSelect = (pkg) => {
    // Redirect to checkout via cart flow – this can be replaced with more complex logic later
    const params = new URLSearchParams();
    params.append('items', pkg.imageCount || 0);
    params.append('package', pkg.id);
    params.append('total', pkg.price);
    window.location.href = `/checkout?${params.toString()}`;
  };

  const openProductModal = async (prod) => {
    try {
      setCurrentProduct(prod);
      setCurrentDetails(null);
      setSelectedVariant('');
      setSelectedImageUrl('');
      onOpen();
      const details = await fetchProduct(shopId, prod.id);
      setCurrentDetails(details);
      if (details?.variants?.length) {
        setSelectedVariant(details.variants[0].id);
      }
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: uploads } = await supabase
          .from('user_uploads')
          .select('file_url, file_url_wm')
          .eq('user_id', user.id)
          .eq('status', 'active');

        const imgs = (uploads || []).map(u => u.file_url_wm || u.file_url);
        setUserImages(imgs);
      }
    } catch (err) {
      console.error('Failed to fetch product details', err);
    }
  };

  const handleAddToCartPrintify = () => {
    if (!currentProduct || !selectedVariant || !selectedImageUrl) {
      alert('Please choose variant and image');
      return;
    }

    addToCart({
      id: `${currentProduct.id}-${selectedVariant}`,
      type: 'printify',
      productId: currentProduct.id,
      variantId: selectedVariant,
      previewUrl: selectedImageUrl,
      name: currentProduct.title,
      price: (currentProduct.variants?.find(v=>v.id===selectedVariant)?.price || 0)/100,
    });
    onClose();
  };

  const handleGenerateMockup = async () => {
    if (!currentProduct || !selectedVariant || !selectedImageUrl) return;
    try {
      setMockupLoading(true);
      setMockupImage('');
      const task = await generateMockup(currentProduct.id, [selectedVariant], selectedImageUrl);
      setMockupTaskId(task?.mockup_task_id || task?.task_id || task?.id);
    } catch (err) {
      console.error('Mock-up generation error', err);
      alert('Failed to start mock-up generation');
    }
  };

  // Poll for mockup when task id changes
  useEffect(() => {
    if (!mockupTaskId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const status = await getMockupStatus(mockupTaskId);
        if (cancelled) return;
        if (status.status === 'completed' && status?.mockups?.length) {
          setMockupImage(status.mockups[0].mockup_url || status.mockups[0].src || status.mockups[0].url);
          setMockupLoading(false);
          setMockupTaskId(null);
        } else if (status.status === 'failed') {
          setMockupLoading(false);
          setMockupTaskId(null);
          alert('Mock-up generation failed');
        } else {
          setTimeout(poll, 3000);
        }
      } catch (err) {
        console.error('Polling error', err);
        setTimeout(poll, 5000);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [mockupTaskId]);

  return (
    <Box>
      <Heading size="lg" mb={2}>Packages</Heading>
      <Text mb={6} color="gray.600">To add products go to your <strong>Pro Edits</strong> and add designs to your cart, then return here to choose the bundle that fits.</Text>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
        {DEFAULT_PACKAGES.map((pkg) => (
          <VStack
            key={pkg.id}
            borderWidth="1px"
            borderRadius="md"
            p={6}
            spacing={4}
            bg="white"
            align="stretch"
          >
            <Heading size="md" textAlign="center">{pkg.name}</Heading>
            <VStack spacing={0} align="center">
              <Text as="s" color="gray.500" fontSize="lg">${pkg.price.toFixed(2)}</Text>
              <Text fontSize="4xl" fontWeight="bold" color="green.600">${(pkg.price*DISCOUNT).toFixed(2)}</Text>
            </VStack>
            <Text textAlign="center" color="gray.600">{pkg.description}</Text>
            <VStack spacing={1} align="start" mt={2}>
              {pkg.features?.map((feat) => (
                <HStack key={feat}>
                  <FaCheckCircle color="#5A9A7E" />
                  <Text fontSize="sm">{feat}</Text>
                </HStack>
              ))}
            </VStack>
          </VStack>
        ))}
      </SimpleGrid>

      {/* Physical Products section removed – now lives at /products */}

      {/* Modal for customization */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Customize Product</ModalHeader>
          <ModalBody>
            {currentDetails ? (
              <VStack align="stretch" spacing={4}>
                {currentDetails?.images?.map(img => (
                  <ChakraImage key={img.id} src={img.src} borderRadius="md" />
                ))}
                <Select
                  value={selectedVariant}
                  onChange={(e) => setSelectedVariant(Number(e.target.value))}
                >
                  {currentDetails.variants
                    ?.filter(v => v.is_enabled)
                    .map(v => (
                      <option key={v.id} value={v.id}>
                        {v.title} – ${(v.price / 100).toFixed(2)}
                      </option>
                    ))}
                </Select>
                <Heading size="sm" mt={4}>
                  Choose an image from your Media Day gallery
                </Heading>
                {userImages.length === 0 && (
                  <Text fontSize="sm" color="gray.500">
                    No uploads found yet – go to Dashboard &gt; Media Day Pics to add some!
                  </Text>
                )}
                <SimpleGrid columns={{ base: 3, md: 4 }} spacing={2}>
                  {userImages.map((u) => (
                    <ChakraImage
                      key={u}
                      src={u}
                      borderRadius="md"
                      cursor="pointer"
                      border={selectedImageUrl === u ? '3px solid #38A169' : '1px solid rgba(0,0,0,0.15)'}
                      onClick={() => setSelectedImageUrl(u)}
                      _hover={{ opacity: 0.85 }}
                    />
                  ))}
                </SimpleGrid>
                <Heading size="sm" mt={4}>
                  …or paste an external image URL
                </Heading>
                <Input
                  value={selectedImageUrl}
                  onChange={(e) => setSelectedImageUrl(e.target.value)}
                  placeholder="https://…"
                />
                <Text whiteSpace="pre-wrap">{currentDetails.description}</Text>
                {mockupImage && (
                  <ChakraImage src={mockupImage} borderRadius="md" />
                )}
                {mockupLoading && (
                  <HStack><Spinner size="sm" /> <Text>Generating preview…</Text></HStack>
                )}
                <Button colorScheme="blue" onClick={handleGenerateMockup} isDisabled={!selectedVariant || !selectedImageUrl || mockupLoading}>
                  {mockupLoading ? 'Generating…' : 'Generate Preview'}
                </Button>
              </VStack>
            ) : (
              <Spinner />
            )}
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={onClose}>Cancel</Button>
            <Button colorScheme="green" onClick={handleAddToCartPrintify} isDisabled={!selectedVariant || !selectedImageUrl}>Add to Cart</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default PackagesPage; 